import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import Icon from './Icon';
import Toast from './Toast';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/recipes';

interface ShoppingItem {
  id: number;
  ingredient_name: string;
  quantity_needed: number;
  quantity_available: number;
  quantity_to_purchase: number;
  unit: string;
  in_stock: boolean;
  recipes_using: string[];
}

const ShoppingList: React.FC = () => {
  const queryClient = useQueryClient();
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const errorDetail = (error: unknown): string | null =>
    axios.isAxiosError(error) ? error.response?.data?.error ?? null : null;

  const { data: items, isLoading } = useQuery({
    queryKey: ['shopping-list'],
    queryFn: async () => {
      const response = await axios.get(`${API_URL}/shopping-list/`);
      return response.data as ShoppingItem[];
    }
  });

  const toPantryMutation = useMutation({
    mutationFn: async (item: ShoppingItem) => {
      const response = await axios.post(`${API_URL}/shopping-list/${item.id}/to-pantry/`);
      return response.data;
    },
    onSuccess: (_data, item) => {
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      queryClient.invalidateQueries({ queryKey: ['inventory'] });
      setMessage({ type: 'success', text: `${item.ingredient_name} added to pantry.` });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error adding item to pantry. Please try again.' });
    },
  });

  if (isLoading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading shopping list...</p>
      </div>
    );
  }

  const itemsToPurchase = items?.filter(item => item.quantity_to_purchase > 0) || [];
  const itemsInStock = items?.filter(item => item.in_stock && item.quantity_to_purchase === 0) || [];

  return (
    <div className="shopping-list">
      <div className="shopping-header">
        <div>
          <h1><Icon name="cart" className="icon" />Shopping List</h1>
          <p>{itemsToPurchase.length} items to purchase</p>
        </div>
      </div>

      {(!items || items.length === 0) ? (
        <div className="empty-state">
          <Icon name="cart" className="icon empty-icon" />
          <h2>No Shopping List Yet</h2>
          <p>Generate a meal plan to create your shopping list.</p>
        </div>
      ) : (
        <>
          {/* Items to Purchase */}
          {itemsToPurchase.length > 0 && (
            <div className="shopping-section">
              <h2 className="section-title">
                <Icon name="cart" className="icon section-icon" />
                Items to Purchase ({itemsToPurchase.length})
              </h2>
              
              <div className="shopping-items">
                {itemsToPurchase.map((item) => (
                  <div key={item.id} className="shopping-item needs-purchase">
                    <div className="item-info">
                      <div className="item-name-row">
                        <h3>{item.ingredient_name}</h3>
                        <span className="purchase-badge">Buy</span>
                      </div>
                      
                      <div className="item-quantities">
                        <div className="quantity-detail">
                          <span className="quantity-label">Need:</span>
                          <span className="quantity-value">
                            {item.quantity_needed} {item.unit}
                          </span>
                        </div>
                        
                        {item.quantity_available > 0 && (
                          <div className="quantity-detail">
                            <span className="quantity-label">Have:</span>
                            <span className="quantity-value available">
                              {item.quantity_available} {item.unit}
                            </span>
                          </div>
                        )}
                        
                        <div className="quantity-detail purchase">
                          <span className="quantity-label">Buy:</span>
                          <span className="quantity-value highlight">
                            {item.quantity_to_purchase} {item.unit}
                          </span>
                        </div>
                      </div>

                      {item.recipes_using && item.recipes_using.length > 0 && (
                        <div className="recipes-using">
                          <span className="recipes-label">Used in:</span>
                          <span className="recipes-list">
                            {item.recipes_using.slice(0, 3).join(', ')}
                            {item.recipes_using.length > 3 && ` +${item.recipes_using.length - 3} more`}
                          </span>
                        </div>
                      )}
                    </div>
                    <button
                      className="btn-secondary in-pantry-btn"
                      onClick={() => toPantryMutation.mutate(item)}
                      disabled={toPantryMutation.isPending}
                    >
                      <Icon name="basket" className="icon icon-sm" />
                      In Pantry
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Items Already in Stock */}
          {itemsInStock.length > 0 && (
            <div className="shopping-section">
              <h2 className="section-title">
                <Icon name="check" className="icon section-icon" />
                Already in Stock ({itemsInStock.length})
              </h2>
              
              <div className="shopping-items">
                {itemsInStock.map((item) => (
                  <div key={item.id} className="shopping-item in-stock">
                    <div className="item-info">
                      <div className="item-name-row">
                        <h3>{item.ingredient_name}</h3>
                        <span className="stock-badge">In Stock</span>
                      </div>
                      
                      <div className="item-quantities">
                        <div className="quantity-detail">
                          <span className="quantity-label">Need:</span>
                          <span className="quantity-value">
                            {item.quantity_needed} {item.unit}
                          </span>
                        </div>
                        
                        <div className="quantity-detail">
                          <span className="quantity-label">Have:</span>
                          <span className="quantity-value available">
                            {item.quantity_available} {item.unit}
                          </span>
                        </div>
                      </div>

                      {item.recipes_using && item.recipes_using.length > 0 && (
                        <div className="recipes-using">
                          <span className="recipes-label">Used in:</span>
                          <span className="recipes-list">
                            {item.recipes_using.slice(0, 3).join(', ')}
                            {item.recipes_using.length > 3 && ` +${item.recipes_using.length - 3} more`}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Summary Card */}
          <div className="shopping-summary">
            <h3><Icon name="bars" className="icon" />Shopping Summary</h3>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-value">{itemsToPurchase.length}</span>
                <span className="stat-label">Items to Buy</span>
              </div>
              <div className="stat">
                <span className="stat-value">{itemsInStock.length}</span>
                <span className="stat-label">Already Have</span>
              </div>
              <div className="stat">
                <span className="stat-value">{items?.length || 0}</span>
                <span className="stat-label">Total Ingredients</span>
              </div>
            </div>
          </div>
        </>
      )}

      {message && (
        <Toast type={message.type} text={message.text} onDismiss={() => setMessage(null)} />
      )}
    </div>
  );
};

export default ShoppingList;